import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Plot from 'react-plotly.js';
import styles from '../../assets/styles/Admin.module.scss';
import arrowIcon from '../../assets/icons/arrow.svg';
import { formatDate } from '../../utils/date';

interface ClientUser {
  id: number;
  userName: string;
  role: string;
  lastLogin: string;
  status: string;
}

interface ClientProject {
  id: number;
  projectName: string;
  platforms: string[];
  chatCount: number;
  diagnosisCount: number;
  proposalCount: number;
  createdAt: string;
  status: string;
}

interface ClientChat {
  id: number;
  user: string;
  date: string;
  message: string;
}

interface ClientPayment {
  id: number;
  amount: number;
  status: string;
  date: string;
}

interface ClientMemo {
  id: number;
  text: string;
  date: string;
}

interface ClientDetailData {
  id: number;
  name: string;
  joinedAt: string;
  plan: string;
  status: string;
  memos?: string;
  metrics: {
    monthlyLogins: number;
    chatCount: number;
    diagnosisCount: number;
    proposalExecutionCount: number;
  };
  companyInfo: {
    corporateNumber: string;
    representative: string;
    contact: string;
    postalCode: string;
    address: string;
    industry: string;
  };
  users: ClientUser[];
  projects: ClientProject[];
  chats?: ClientChat[];
  payments?: ClientPayment[];
  salesInfo?: {
    hearingStatus: string;
    hearingDate?: string;
    brand?: string;
    monthlyBudget?: number;
    knowledgeLevel?: string;
    platforms?: string[];
    targetCTR?: number;
    targetCVR?: number;
    agencyNeeds?: string;
    memo?: string;
  };
}

const AdminClientDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [clientData, setClientData] = useState<ClientDetailData | null>(null);
  const [loading, setLoading] = useState(true);

  // State for editable fields and memos
  const [plan, setPlan] = useState('');
  const [status, setStatus] = useState('');
  const [memos, setMemos] = useState<ClientMemo[]>([]);
  const [newMemoText, setNewMemoText] = useState('');

  // Fallback mock data for chart since DB doesn't have history
  const mockBudgetHistory = {
    months: ['2023-11', '2023-12', '2024-01', '2024-02', '2024-03', '2024-04'],
    google: [1200000, 1300000, 1100000, 1400000, 1500000, 1600000],
    meta: [800000, 900000, 850000, 950000, 1000000, 1100000],
    yahoo: [300000, 350000, 300000, 400000, 400000, 450000]
  };

  useEffect(() => {
    const fetchDetail = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/admin/clients/${id}`, {
          credentials: 'include'
        });
        if (res.ok) {
          const data = await res.json();
          setClientData(data);
          setPlan(data.plan);
          setStatus(data.status);

          if (data.memos) {
            setMemos([{
              id: 1,
              text: data.memos,
              date: new Date().toISOString().split('T')[0] // Approximation since we only have a single memo field
            }]);
          }
        }
      } catch (error) {
        console.error('Failed to fetch client detail', error);
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [id]);

  const handleEdit = () => {
    alert('ダミーの編集モーダルが開きます（モック）');
  };

  const handleAddMemo = async () => {
    if (!newMemoText.trim()) return;

    // For MVP, we just overwrite the single internal_memo field with the new text.
    // In a real system, we'd append or have a separate memo table.
    const newMemoContent = memos.length > 0 ? `${memos[0].text}\n\n[追記]: ${newMemoText}` : newMemoText;

    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/admin/clients/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ internal_memo: newMemoContent })
      });
      if (res.ok) {
        setMemos([{
          id: Date.now(),
          text: newMemoContent,
          date: new Date().toISOString().split('T')[0]
        }]);
        setNewMemoText('');
      } else {
        alert('メモの保存に失敗しました。');
      }
    } catch (error) {
      console.error(error);
      alert('通信エラーが発生しました。');
    }
  };

  const handleDeleteMemo = async (memoId: number) => {
    if (!window.confirm('メモを削除しますか？')) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/admin/clients/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ internal_memo: null }) // clear memo
      });
      if (res.ok) {
        setMemos([]);
      }
    } catch (error) {
      console.error(error);
    }
  };

  if (loading) return <div className={styles.detailWrap}><p className={styles.loadingText}>読み込み中...</p></div>;
  if (!clientData) return <div className={styles.detailWrap}><p className={styles.loadingText}>データが見つかりません</p></div>;

  // Calculate total budget for the overlay bar chart using mock data
  const totalBudgets = mockBudgetHistory.google.map((googleVal: number, i: number) => {
    return googleVal + mockBudgetHistory.meta[i] + mockBudgetHistory.yahoo[i];
  });

  return (
    <div className={styles.detailWrap}>
      {/* Main Content (Left) */}
      <div className={styles.detailMain}>

        {/* Header */}
        <div className={styles.detailHeader}>
          <div className={styles.headerLeft}>
            <button onClick={() => navigate(-1)} className={styles.backButton}>
              <img src={arrowIcon} alt="戻る" />
            </button>
            <div className={styles.headerInfo}>
              <h1>
                {clientData.name}
              </h1>
              <p>ID: {clientData.id} | 登録日: {formatDate(clientData.joinedAt)}</p>
            </div>
          </div>
          <button onClick={handleEdit} className={styles.editButton}>
            編集
          </button>
        </div>

        {/* Metrics (4 cards in one row) */}
        <div className={styles.metricsGrid}>
          <div className={`${styles.contentBox} ${styles.metricCard}`}>
            <h3>月間ログイン</h3>
            <p>{clientData.metrics.monthlyLogins}</p>
          </div>
          <div className={`${styles.contentBox} ${styles.metricCard}`}>
            <h3>チャット回数</h3>
            <p>{clientData.metrics.chatCount}</p>
          </div>
          <div className={`${styles.contentBox} ${styles.metricCard}`}>
            <h3>診断回数</h3>
            <p>{clientData.metrics.diagnosisCount}</p>
          </div>
          <div className={`${styles.contentBox} ${styles.metricCard}`}>
            <h3>提案実行回数</h3>
            <p>{clientData.metrics.proposalExecutionCount}</p>
          </div>
        </div>

        {/* Company Info */}
        <div className={styles.contentBox}>
          <h2 className={styles.sectionTitle}>企業情報</h2>
          <div className={styles.infoGrid}>
            <div className={styles.infoItem}>
              <p className={styles.infoLabel}>企業名</p>
              <p className={styles.infoValue}>{clientData.name}</p>
            </div>
            <div className={styles.infoItem}>
              <p className={styles.infoLabel}>法人番号</p>
              <p className={styles.infoValue}>{clientData.companyInfo.corporateNumber}</p>
            </div>
            <div className={styles.infoItem}>
              <p className={styles.infoLabel}>代表者</p>
              <p className={styles.infoValue}>{clientData.companyInfo.representative}</p>
            </div>
            <div className={styles.infoItem}>
              <p className={styles.infoLabel}>連絡先</p>
              <p className={styles.infoValue}>{clientData.companyInfo.contact}</p>
            </div>
            <div className={styles.infoItem}>
              <p className={styles.infoLabel}>所在地</p>
              <p className={styles.infoValue}>〒{clientData.companyInfo.postalCode} {clientData.companyInfo.address}</p>
            </div>
            <div className={styles.infoItem}>
              <p className={styles.infoLabel}>業種</p>
              <p className={styles.infoValue}>{clientData.companyInfo.industry}</p>
            </div>
          </div>
        </div>

        {/* Users List (Moved right below Company Info) */}
        <div className={styles.contentBox}>
          <h2 className={styles.sectionTitle}>所属ユーザー</h2>
          <div className={styles.tableWrap}>
            <table className={styles.adminTable}>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>ユーザー名</th>
                  <th>権限</th>
                  <th>最終ログイン</th>
                  <th>ステータス</th>
                </tr>
              </thead>
              <tbody>
                {(clientData.users || []).map((u: ClientUser) => (
                  <tr key={`u-${u.id}`}>
                    <td>{u.id}</td>
                    <td>{u.userName}</td>
                    <td>{u.role}</td>
                    <td>{formatDate(u.lastLogin)}</td>
                    <td>
                      <span className={`${styles.badge} ${styles[u.status]}`}>{u.status.toUpperCase()}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Projects List */}
        <div className={styles.contentBox}>
          <h2 className={styles.sectionTitle}>関連プロジェクト</h2>
          <div className={styles.tableWrap}>
            <table className={styles.adminTable}>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>プロジェクト名</th>
                  <th>連携媒体</th>
                  <th>チャット数</th>
                  <th>診断数</th>
                  <th>提案数</th>
                  <th>作成日</th>
                  <th>ステータス</th>
                </tr>
              </thead>
              <tbody>
                {(clientData.projects || []).map((p: ClientProject) => (
                  <tr key={`p-${p.id}`}>
                    <td>{p.id}</td>
                    <td>{p.projectName}</td>
                    <td>
                      <div className={styles.platformBadgeWrap}>
                        {p.platforms && p.platforms.length > 0 ? (
                          p.platforms.map((pf: string) => (
                            <span key={pf} className={`${styles.badge} ${styles.active} ${styles.platformBadgeSmall}`}>{pf}</span>
                          ))
                        ) : (
                          <span className={styles.emptyText}>-</span>
                        )}
                      </div>
                    </td>
                    <td>{p.chatCount}</td>
                    <td>{p.diagnosisCount}</td>
                    <td>{p.proposalCount}</td>
                    <td>{formatDate(p.createdAt)}</td>
                    <td>
                      <span className={`${styles.badge} ${styles[p.status]}`}>{p.status.toUpperCase()}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!(clientData.projects?.length) && <p className={styles.emptyTableText}>プロジェクトはありません</p>}
          </div>
        </div>

        {/* Ad Platforms & Budget Chart */}
        <div className={styles.contentBox}>
          <div className={styles.sectionTitle}>
            <span>広告予算推移</span>
          </div>

          <div className={styles.chartContainer}>
            <Plot
              data={[
                // Bar chart must be first to render behind the lines
                {
                  x: mockBudgetHistory.months,
                  y: totalBudgets,
                  type: 'bar',
                  name: '総予算',
                  marker: { color: 'rgba(255, 255, 255, 0.05)' },
                  hoverinfo: 'y+name'
                },
                {
                  x: mockBudgetHistory.months,
                  y: mockBudgetHistory.google,
                  type: 'scatter',
                  mode: 'lines+markers',
                  name: 'Google Ads',
                  line: { color: '#4285F4', width: 3 },
                  marker: { size: 8 }
                },
                {
                  x: mockBudgetHistory.months,
                  y: mockBudgetHistory.meta,
                  type: 'scatter',
                  mode: 'lines+markers',
                  name: 'Meta Ads',
                  line: { color: '#0668E1', width: 3 },
                  marker: { size: 8 }
                },
                {
                  x: mockBudgetHistory.months,
                  y: mockBudgetHistory.yahoo,
                  type: 'scatter',
                  mode: 'lines+markers',
                  name: 'Yahoo! Ads',
                  line: { color: '#FF0033', width: 3 },
                  marker: { size: 8 }
                }
              ]}
              layout={{
                autosize: true,
                barmode: 'group',
                margin: { t: 20, r: 20, b: 40, l: 60 },
                paper_bgcolor: 'transparent',
                plot_bgcolor: 'transparent',
                font: { color: '#b3b3b3' },
                xaxis: { gridcolor: 'rgba(255,255,255,0.1)' },
                yaxis: { gridcolor: 'rgba(255,255,255,0.1)', tickprefix: '¥' },
                legend: { orientation: 'h', y: -0.2 }
              }}
              useResizeHandler={true}
              style={{ width: '100%', height: '100%' }}
              config={{ displayModeBar: false }}
            />
          </div>

          {/* Ad Platforms Status List (Moved below chart) */}
          <div className={styles.adPlatformsSection}>
            <h3 className={styles.adPlatformsTitle}>広告PF連携状況</h3>
            <div className={styles.adPlatformList}>
              {(() => {
                const connectedPlatforms = new Set((clientData.projects || []).flatMap((p: ClientProject) => p.platforms || []));
                const allPlatforms = ['Google Ads', 'Meta Ads', 'Yahoo Ads', 'GA4'];
                return allPlatforms.map(pf => {
                  const isConnected = connectedPlatforms.has(pf);
                  return (
                    <div key={pf} className={`${styles.adPlatformItem} ${!isConnected ? styles.adPlatformItemDisconnected : ''}`}>
                      <span className={`${styles.adPlatformDot} ${!isConnected ? styles.adPlatformDotDisconnected : ''}`}>●</span>
                      <span className={`${styles.adPlatformName} ${!isConnected ? styles.adPlatformNameDisconnected : ''}`}>{pf}</span>
                      <span className={`${styles.adPlatformStatusLabel} ${!isConnected ? styles.adPlatformStatusLabelDisconnected : ''}`}>
                        {isConnected ? '連携済み' : '未接続'}
                      </span>
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        </div>

        {/* Chat History */}
        <div className={styles.contentBox}>
          <h2 className={styles.sectionTitle}>最近のチャット内容</h2>
          <div className={styles.chatList}>
            {(clientData.chats || []).map((chat: ClientChat) => (
              <div key={`c-${chat.id}`} className={styles.chatItem}>
                <div className={styles.chatHeader}>
                  <span className={styles.chatUser}>{chat.user}</span>
                  <span className={styles.chatDate}>{chat.date}</span>
                </div>
                <p className={styles.chatMessage}>{chat.message}</p>
              </div>
            ))}
            {!(clientData.chats?.length) && <p className={styles.emptyText}>チャット履歴はありません</p>}
          </div>
        </div>

        {/* Sales Info */}
        {clientData.salesInfo && (
          <div className={styles.contentBox}>
            <h2 className={styles.sectionTitle}>営業情報（ヒアリング内容）</h2>
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <p className={styles.infoLabel}>ヒアリング状況</p>
                <p className={styles.infoValue}>{clientData.salesInfo.hearingStatus}</p>
              </div>
              <div className={styles.infoItem}>
                <p className={styles.infoLabel}>ヒアリング日付</p>
                <p className={styles.infoValue}>{clientData.salesInfo.hearingDate ? formatDate(clientData.salesInfo.hearingDate) : '-'}</p>
              </div>

              {clientData.salesInfo.hearingStatus === '実施済' && (
                <>
                  <div className={`${styles.infoItem} ${styles.fullWidth}`}>
                    <p className={styles.infoLabel}>広告運用しているブランド</p>
                    <p className={styles.infoValue}>{clientData.salesInfo.brand || '-'}</p>
                  </div>
                  <div className={styles.infoItem}>
                    <p className={styles.infoLabel}>月間の広告予算</p>
                    <p className={styles.infoValue}>
                      {clientData.salesInfo.monthlyBudget
                        ? `${Number(clientData.salesInfo.monthlyBudget).toLocaleString()}円`
                        : '-'}
                    </p>
                  </div>
                  <div className={styles.infoItem}>
                    <p className={styles.infoLabel}>広告運用知識</p>
                    <p className={styles.infoValue}>{clientData.salesInfo.knowledgeLevel || '-'}</p>
                  </div>
                  <div className={`${styles.infoItem} ${styles.fullWidth}`}>
                    <p className={styles.infoLabel}>利用している媒体</p>
                    <div className={styles.infoBadgeWrap}>
                      {clientData.salesInfo.platforms && clientData.salesInfo.platforms.length > 0 ? (
                        clientData.salesInfo.platforms.map((pf: string) => (
                          <span key={pf} className={`${styles.badge} ${styles.active}`}>{pf}</span>
                        ))
                      ) : (
                        <p className={styles.infoValue}>-</p>
                      )}
                    </div>
                  </div>
                  <div className={styles.infoItem}>
                    <p className={styles.infoLabel}>目標CTR</p>
                    <p className={styles.infoValue}>{clientData.salesInfo.targetCTR ? `${clientData.salesInfo.targetCTR}%` : '-'}</p>
                  </div>
                  <div className={styles.infoItem}>
                    <p className={styles.infoLabel}>目標CVR</p>
                    <p className={styles.infoValue}>{clientData.salesInfo.targetCVR ? `${clientData.salesInfo.targetCVR}%` : '-'}</p>
                  </div>
                  <div className={`${styles.infoItem} ${styles.fullWidth}`}>
                    <p className={styles.infoLabel}>広告運用代行ニーズ</p>
                    <p className={styles.infoValue}>{clientData.salesInfo.agencyNeeds || '-'}</p>
                  </div>
                </>
              )}

              <div className={`${styles.infoItem} ${styles.fullWidth}`}>
                <p className={`${styles.infoLabel} ${styles.infoLabel} ${styles.infoLabelMargin}`}>備考（ヒアリングメモ）</p>
                <div className={styles.notesBox}>
                  <p>{clientData.salesInfo.memo || 'メモはありません'}</p>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>

      {/* Right Sidebar */}
      <div className={styles.detailSidebar}>

        {/* Plan & Status Form */}
        <div className={styles.contentBox}>
          <h2 className={styles.sectionTitle}>契約情報</h2>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>現在のプラン</label>
            <select
              className={styles.filterSelect}
              style={{ width: '100%' }}
              value={plan}
              onChange={async (e) => {
                const newPlan = e.target.value;
                setPlan(newPlan);
                try {
                  await fetch(`${process.env.REACT_APP_BACKEND_URL}/admin/clients/${id}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({ plan: newPlan })
                  });
                } catch (e) {
                  console.error(e);
                }
              }}
            >
              <option value="standard">standard</option>
              <option value="test">test</option>
            </select>
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>ステータス</label>
            <select
              className={styles.filterSelect}
              style={{ width: '100%' }}
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="active">active</option>
              <option value="inactive">inactive</option>
            </select>
          </div>
        </div>

        {/* Payment History */}
        <div className={styles.contentBox}>
          <h2 className={styles.sectionTitle}>決済履歴</h2>
          <div className={styles.paymentList}>
            {(clientData.payments || []).map((payment: ClientPayment) => (
              <div key={`p-${payment.id}`} className={styles.paymentItem}>
                <div className={styles.paymentRow}>
                  <span className={styles.paymentAmount}>¥{payment.amount.toLocaleString()}</span>
                  <span className={`${styles.badge} ${styles[payment.status]}`}>
                    {payment.status.toUpperCase()}
                  </span>
                </div>
                <div className={styles.paymentMeta}>{payment.date} | INV-{payment.id}</div>
              </div>
            ))}
            {!(clientData.payments?.length) && <p className={styles.emptyText}>決済履歴はありません</p>}
          </div>
        </div>

        {/* Memos */}
        <div className={styles.contentBox}>
          <h2 className={styles.sectionTitle}>社内メモ</h2>
          <div>
            {memos.length === 0 ? (
              <p className={styles.memoEmpty}>メモはありません</p>
            ) : (
              memos.map((memo: ClientMemo) => (
                <div key={memo.id} className={styles.memoItem}>
                  <p>{memo.text}</p>
                  <span className={styles.memoDate}>{memo.date}</span>
                  <button className={styles.memoDelete} onClick={() => handleDeleteMemo(memo.id)}>×</button>
                </div>
              ))
            )}

            <div className={styles.memoAddBox}>
              <textarea
                placeholder="新しいメモを入力..."
                value={newMemoText}
                onChange={(e) => setNewMemoText(e.target.value)}
              />
              <button onClick={handleAddMemo}>追加</button>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default AdminClientDetail;
